import React, { useState } from 'react';

const MembershipApplicationForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    areaOfSpecialty: '',
    phoneNumber: '',
    email: '',
    streetAddress: '',
    permanentAddress: '',
    mdcnRegistrationNumber: '',
    yearQualifiedMBBS: '',
    additionalQualificationMDCN: '',
    yearQualifiedUrologist: '',
    currentPractice: '',
    nextOfKinName: '',
    nextOfKinPhone: '',
    nextOfKinEmail: '',
    fellowshipCollege: '',
    fwacs: false,
    fmcs: false,
    facs: false,
    frcs: false,
    others: false,
    qualificationYear: '',
    additionalQualification: '',
    residencyTraining: '',
    foreignInstitution: '',
    conferenceAttended: '',
    declaration: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [documents, setDocuments] = useState({
    mbbsCertificate: null,
    fellowshipCertificate: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setDocuments({
      ...documents,
      [name]: files[0]
    });
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber || 
        !formData.areaOfSpecialty || !formData.mdcnRegistrationNumber || !formData.yearQualifiedMBBS ||
        !formData.additionalQualificationMDCN || !formData.yearQualifiedUrologist || !formData.currentPractice ||
        !formData.nextOfKinName || !formData.nextOfKinPhone || !formData.nextOfKinEmail ||
        !formData.fellowshipCollege || !formData.qualificationYear || !formData.additionalQualification ||
        !formData.residencyTraining || !formData.declaration) {
      setSubmitError('Please fill in all required fields');
      return false;
    }
    if (!documents.mbbsCertificate || !documents.fellowshipCertificate) {
      setSubmitError('Please upload both required documents');
      return false;
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      setSubmitError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Create FormData object to handle files
      const formDataToSend = new FormData();
      
      // Add all text fields to FormData
      Object.keys(formData).forEach(key => {
        if (key !== 'mbbsCertificate' && key !== 'fellowshipCertificate') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add file fields if they exist
      if (documents.mbbsCertificate) {
        formDataToSend.append('mbbsCertificate', documents.mbbsCertificate);
      }
      if (documents.fellowshipCertificate) {
        formDataToSend.append('fellowshipCertificate', documents.fellowshipCertificate);
      }

      console.log('Sending form data to backend...');
      
      // Send to backend - IMPORTANT: Don't set Content-Type header when using FormData
      const response = await fetch('https://naus-backend.onrender.com/api/applications', {
        method: 'POST',
        // DON'T set Content-Type header - browser sets it automatically with boundary
        body: formDataToSend
      });

      console.log('Response status:', response.status);

      // Check if response is ok first
      if (!response.ok) {
        // Handle non-200 responses
        let errorText = '';
        try {
          // Try to parse as JSON first
          const errorJson = await response.json();
          errorText = errorJson.message || `HTTP Error: ${response.status} ${response.statusText}`;
        } catch (jsonError) {
          // If JSON parsing fails, get text response
          errorText = await response.text();
          console.error('Error response (not JSON):', errorText);
        }
        
        setSubmitError(`Submission failed: ${errorText}`);
        return;
      }

      // Try to parse JSON response
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Error parsing JSON response:', parseError);
        console.error('Response status:', response.status);
        console.error('Response headers:', response.headers.get('content-type'));
        
        // Try to get the response text to see what we got
        const responseText = await response.text();
        console.error('Raw response:', responseText);
        
        throw new Error('Server returned invalid response format. Please contact support.');
      }

      console.log('Response status:', response.status);
      console.log('Response ', result);

      if (response.status === 201) { // 201 Created
        setSubmitSuccess(true);
        setSubmitError('');
        
        // Reset form after successful submission
        setFormData({
          firstName: '',
          middleName: '',
          lastName: '',
          areaOfSpecialty: '',
          phoneNumber: '',
          email: '',
          streetAddress: '',
          permanentAddress: '',
          mdcnRegistrationNumber: '',
          yearQualifiedMBBS: '',
          additionalQualificationMDCN: '',
          yearQualifiedUrologist: '',
          currentPractice: '',
          nextOfKinName: '',
          nextOfKinPhone: '',
          nextOfKinEmail: '',
          fellowshipCollege: '',
          fwacs: false,
          fmcs: false,
          facs: false,
          frcs: false,
          others: false,
          qualificationYear: '',
          additionalQualification: '',
          residencyTraining: '',
          foreignInstitution: '',
          conferenceAttended: '',
          declaration: '',
          date: new Date().toISOString().split('T')[0]
        });
        setDocuments({
          mbbsCertificate: null,
          fellowshipCertificate: null
        });
      } else {
        setSubmitError(result.message || 'Submission failed with unexpected status');
        console.error('Submission error:', result);
      }
      
    } catch (error) {
      console.error('Network error:', error);
      if (error.message.includes('invalid response format')) {
        setSubmitError(error.message);
      } else if (error.message.includes('Failed to fetch')) {
        setSubmitError('Network error: Cannot connect to server. Please try again later.');
      } else {
        setSubmitError(`Network error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div style={{
        backgroundColor: 'var(--white)',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: 'var(--shadow)',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '2rem auto'
      }}>
        <h3 style={{ color: 'var(--accent-green)', marginBottom: '1rem' }}>Application Submitted Successfully!</h3>
        <p>Thank you for your interest in NAUS membership.</p>
        <p>Your application has been received and will be reviewed by our Executive Committee.</p>
        <p style={{ marginTop: '1rem', fontStyle: 'italic', color: '#666' }}>
          <em>You will receive an email notification once your application has been reviewed.</em>
        </p>
        <p style={{ marginTop: '1rem' }}>
          <strong>Important:</strong> Once approved, you will receive your membership number and a temporary password via email.
        </p>
        <p style={{ marginTop: '0.5rem' }}>
          Use this information to log in and your password can be reset from your settings after logging in.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--white)',
      padding: '2rem',
      borderRadius: '8px',
      boxShadow: 'var(--shadow)',
      maxWidth: '800px',
      margin: '2rem auto'
    }}>
      <h2 style={{ color: 'var(--primary-green)', textAlign: 'center', marginBottom: '2rem' }}>Membership Application Form</h2>
      
      {submitError && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label htmlFor="firstName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label htmlFor="middleName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Middle Name
            </label>
            <input
              type="text"
              id="middleName"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="lastName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Last Name *
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="areaOfSpecialty" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Area of Specialty *
          </label>
          <input
            type="text"
            id="areaOfSpecialty"
            name="areaOfSpecialty"
            value={formData.areaOfSpecialty}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label htmlFor="phoneNumber" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Phone Number *
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="streetAddress" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Street Address *
          </label>
          <textarea
            id="streetAddress"
            name="streetAddress"
            value={formData.streetAddress}
            onChange={handleChange}
            required
            rows="3"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="permanentAddress" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Permanent Address *
          </label>
          <textarea
            id="permanentAddress"
            name="permanentAddress"
            value={formData.permanentAddress}
            onChange={handleChange}
            required
            rows="3"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label htmlFor="mdcnRegistrationNumber" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              MDCN Registration Number *
            </label>
            <input
              type="text"
              id="mdcnRegistrationNumber"
              name="mdcnRegistrationNumber"
              value={formData.mdcnRegistrationNumber}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label htmlFor="yearQualifiedMBBS" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Year Qualified MBBS *
            </label>
            <input
              type="number"
              id="yearQualifiedMBBS"
              name="yearQualifiedMBBS"
              value={formData.yearQualifiedMBBS}
              onChange={handleChange}
              required
              min="1900"
              max="2026"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="additionalQualificationMDCN" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Additional Qualification MDCN *
          </label>
          <input
            type="text"
            id="additionalQualificationMDCN"
            name="additionalQualificationMDCN"
            value={formData.additionalQualificationMDCN}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label htmlFor="yearQualifiedUrologist" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Year Qualified Urologist *
            </label>
            <input
              type="number"
              id="yearQualifiedUrologist"
              name="yearQualifiedUrologist"
              value={formData.yearQualifiedUrologist}
              onChange={handleChange}
              required
              min="1900"
              max="2026"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label htmlFor="currentPractice" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Current Practice *
            </label>
            <input
              type="text"
              id="currentPractice"
              name="currentPractice"
              value={formData.currentPractice}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>Next of Kin Information *</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label htmlFor="nextOfKinName" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Name *
              </label>
              <input
                type="text"
                id="nextOfKinName"
                name="nextOfKinName"
                value={formData.nextOfKinName}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label htmlFor="nextOfKinPhone" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Phone *
              </label>
              <input
                type="tel"
                id="nextOfKinPhone"
                name="nextOfKinPhone"
                value={formData.nextOfKinPhone}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          <div>
            <label htmlFor="nextOfKinEmail" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Email *
            </label>
            <input
              type="email"
              id="nextOfKinEmail"
              name="nextOfKinEmail"
              value={formData.nextOfKinEmail}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="fellowshipCollege" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Fellowship College *
          </label>
          <input
            type="text"
            id="fellowshipCollege"
            name="fellowshipCollege"
            value={formData.fellowshipCollege}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>Qualifications</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {['fwacs', 'fmcs', 'facs', 'frcs', 'others'].map((qualification) => (
              <div key={qualification} style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id={qualification}
                  name={qualification}
                  checked={formData[qualification]}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem' }}
                />
                <label htmlFor={qualification} style={{ fontWeight: 'bold' }}>
                  {qualification.toUpperCase()}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label htmlFor="qualificationYear" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Year of Qualification *
            </label>
            <input
              type="number"
              id="qualificationYear"
              name="qualificationYear"
              value={formData.qualificationYear}
              onChange={handleChange}
              required
              min="1900"
              max="2026"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label htmlFor="additionalQualification" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Additional Qualification *
            </label>
            <input
              type="text"
              id="additionalQualification"
              name="additionalQualification"
              value={formData.additionalQualification}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="residencyTraining" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Residency Training *
          </label>
          <textarea
            id="residencyTraining"
            name="residencyTraining"
            value={formData.residencyTraining}
            onChange={handleChange}
            required
            rows="3"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label htmlFor="foreignInstitution" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Foreign Institution
            </label>
            <input
              type="text"
              id="foreignInstitution"
              name="foreignInstitution"
              value={formData.foreignInstitution}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label htmlFor="conferenceAttended" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Conference Attended
            </label>
            <input
              type="text"
              id="conferenceAttended"
              name="conferenceAttended"
              value={formData.conferenceAttended}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="date" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Date *
          </label>
          <input
            type="date"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="declaration" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Declaration *
          </label>
          <textarea
            id="declaration"
            name="declaration"
            value={formData.declaration}
            onChange={handleChange}
            required
            rows="4"
            placeholder="I declare that the information provided above is true and accurate..."
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>Required Documents *</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label htmlFor="mbbsCertificate" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                MBBS Certificate
              </label>
              <input
                type="file"
                id="mbbsCertificate"
                name="mbbsCertificate"
                onChange={handleFileChange}
                required
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"  // ← Updated to accept more file types
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label htmlFor="fellowshipCertificate" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Fellowship Certificate
              </label>
              <input
                type="file"
                id="fellowshipCertificate"
                name="fellowshipCertificate"
                onChange={handleFileChange}
                required
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"  // ← Updated to accept more file types
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn"
          style={{
            width: '100%',
            padding: '1rem',
            fontSize: '1.1rem',
            cursor: isSubmitting ? 'not-allowed' : 'pointer'
          }}
        >
          {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
        </button>

        <p style={{ 
          textAlign: 'center', 
          marginTop: '1rem', 
          fontSize: '0.8rem', 
          color: '#666',
          fontStyle: 'italic'
        }}>
          * Required fields
        </p>
      </form>
    </div>
  );
};

export default MembershipApplicationForm;